import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { message } from 'antd';
import api from '../services/api';
import { User } from '../types';
import { getCentralLogoutUrl, getCentralLoginRedirect } from '../config';
import { canRedirectToAuth, markRedirectToAuth, clearRedirectGuard } from '../utils/authRedirectGuard';

interface SsoSessionResponse {
  authenticated: boolean;
  access_token?: string;
  user?: User;
  /** Локальный режим: входа через SSO нет, пользователя выбирают на /login. */
  dev_login?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loginWithSso: (accessToken: string, userData: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

let authMeFetchInFlight = false;
let authMeFetchedOnce = false;

function goToLocalLogin(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
}

function getAuthReturnUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  if (window.location.pathname === '/login') {
    return `${window.location.origin}/`;
  }
  return window.location.href;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const redirectSent = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[AUTH] initializeAuth called');
    }

    const params = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
    const tokenFromUrl = params.get('token') ?? params.get('access_token');
    if (tokenFromUrl && typeof window !== 'undefined') {
      const userStr = params.get('user');
      let userData: User | null = null;
      if (userStr) {
        try {
          userData = JSON.parse(decodeURIComponent(userStr)) as User;
        } catch {
          userData = null;
        }
      }
      if (userData) {
        clearRedirectGuard();
        setToken(tokenFromUrl);
        setUser(userData);
        if (process.env.NODE_ENV === 'development') console.log('[AUTH] setUser (from URL)');
        localStorage.setItem('token', tokenFromUrl);
        localStorage.setItem('user', JSON.stringify(userData));
        api.defaults.headers.common['Authorization'] = `Bearer ${tokenFromUrl}`;
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
        return;
      }
    }

    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(userData);
        api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;

        if (authMeFetchInFlight || authMeFetchedOnce) {
          setLoading(false);
          return;
        }
        authMeFetchInFlight = true;

        const timeout = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), 8000)
        );

        if (process.env.NODE_ENV === 'development') console.log('[AUTH] fetch /auth/me');

        // ВАЖНО: используем вложенную цепочку, а НЕ .then().catch().then().
        // Плоская цепочка (.then(A).catch(B).then(C)) вызывает C даже при успехе A
        // (с ssoRes = undefined), что приводит к бесконечному редиректу.
        Promise.race([api.get('/auth/me'), timeout])
          .then((response: any) => {
            // /auth/me УСПЕХ — sso-session НЕ вызывается
            authMeFetchedOnce = true;
            authMeFetchInFlight = false;
            clearRedirectGuard();
            if (process.env.NODE_ENV === 'development') console.log('[AUTH] setUser (from /auth/me)');
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            setLoading(false);
          })
          .catch((error) => {
            // /auth/me ОШИБКА — только здесь проверяем sso-session
            authMeFetchedOnce = false;
            authMeFetchInFlight = false;
            if (error?.message !== 'timeout') {
              console.warn('Токен невалиден, проверка SSO…');
            }
            setToken(null);
            setUser(null);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete api.defaults.headers.common['Authorization'];

            api.get<SsoSessionResponse>('/auth/sso-session', { withCredentials: true })
              .then(({ data }) => {
                if (data.authenticated === true && data.access_token && data.user) {
                  clearRedirectGuard();
                  if (process.env.NODE_ENV === 'development') console.log('[AUTH] setUser (from sso-session after /auth/me fail)');
                  setToken(data.access_token);
                  setUser(data.user);
                  localStorage.setItem('token', data.access_token);
                  localStorage.setItem('user', JSON.stringify(data.user));
                  api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
                } else if (data.dev_login) {
                  goToLocalLogin();
                } else if (!redirectSent.current && canRedirectToAuth()) {
                  redirectSent.current = true;
                  markRedirectToAuth();
                  window.location.replace(getCentralLoginRedirect(getAuthReturnUrl()));
                }
              })
              .catch(() => {
                if (!redirectSent.current && canRedirectToAuth()) {
                  redirectSent.current = true;
                  markRedirectToAuth();
                  window.location.replace(getCentralLoginRedirect(getAuthReturnUrl()));
                }
              })
              .finally(() => setLoading(false));
          });
      } catch (error) {
        console.error('Ошибка при загрузке сохраненных данных:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (!redirectSent.current && canRedirectToAuth()) {
          redirectSent.current = true;
          markRedirectToAuth();
          window.location.replace(getCentralLoginRedirect(getAuthReturnUrl()));
        }
        setLoading(false);
      }
      return;
    }

    api.get<SsoSessionResponse>('/auth/sso-session', { withCredentials: true })
      .then(({ data }) => {
        if (data.authenticated === true && data.access_token && data.user) {
          clearRedirectGuard();
          if (process.env.NODE_ENV === 'development') console.log('[AUTH] setUser (from sso-session, no saved token)');
          setToken(data.access_token);
          setUser(data.user);
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
        } else if (data.dev_login) {
          goToLocalLogin();
        } else if (!redirectSent.current && canRedirectToAuth()) {
          redirectSent.current = true;
          markRedirectToAuth();
          window.location.replace(getCentralLoginRedirect(getAuthReturnUrl()));
        }
      })
      .catch(() => {
        if (!redirectSent.current && canRedirectToAuth()) {
          redirectSent.current = true;
          markRedirectToAuth();
          window.location.replace(getCentralLoginRedirect(getAuthReturnUrl()));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const loginWithSso = useCallback((accessToken: string, userData: User) => {
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    message.success('Вход через PikinLab выполнен');
  }, []);

  const logout = () => {
    authMeFetchInFlight = false;
    authMeFetchedOnce = false;
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    message.info('Выход из системы…');
    const logoutUrl = getCentralLogoutUrl();
    const next = typeof window !== 'undefined' ? encodeURIComponent(window.location.origin) : '';
    window.location.href = next ? `${logoutUrl}?next=${next}` : logoutUrl;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loginWithSso,
        logout,
        isAuthenticated: !!token && !!user,
        isAdmin: user?.role === 'admin',
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
